import { SerializedConstructor } from '@langchain/core/load/serializable';
import { Chip, FormLabel } from '@mui/joy';

import { getModelDisplayNameFromIdList } from '@/Pages/Playground/Playground.constants';
import { EPromptType } from '@/types/schema';
import { cn } from '@/utils/tailwind';

import {
  ManifestEditorProps,
  ManifestPreviewProps,
  tableVariantContainerClass,
} from '../../Manifest.utils';
import { ManifestNavigation } from '../../ManifestNavigation';
import { ManifestSelectModel } from '../../ManifestSelectModel';
import { ManifestProviderReplace } from '../../ManifestSelectProvider';
import { ManifestSlider } from '../../ManifestSlider';
import { ManifestStopEditor } from '../../ManifestStopEditor';
import { DEFAULT_MODEL } from './ManifestChatGroq.utils';

export function ManifestChatGroq(
  props: ManifestEditorProps<SerializedConstructor>
) {
  const kwargs = props.value.kwargs;

  return (
    <>
      <ManifestNavigation navigation={props.navigation} />

      <div
        className={cn(
          'flex flex-col gap-3',
          props.variant === 'table' && tableVariantContainerClass
        )}
      >
        <ManifestProviderReplace
          variant={props.variant}
          promptType={EPromptType.CHAT}
          onChange={props.onChange}
          value={props.value}
          readOnly={props.readOnly}
        />

        <ManifestSelectModel
          variant={props.variant}
          models={[]}
          dynamic={{
            id: props.value.id,
            secrets: props.secrets,
          }}
          onChange={(model) => {
            props.onChange({
              ...props.value,
              kwargs: { ...kwargs, model },
            });
          }}
          value={kwargs.model ?? DEFAULT_MODEL}
          readOnly={props.readOnly}
        />

        <ManifestSlider
          title="Temperature"
          value={kwargs.temperature}
          min={0}
          max={2}
          step={0.1}
          onChange={(temperature) =>
            props.onChange({
              ...props.value,
              kwargs: { ...kwargs, temperature },
            })
          }
          readOnly={props.readOnly}
          variant={props.variant}
        />

        <ManifestSlider
          title="Max Output Tokens"
          value={kwargs.max_tokens}
          min={-1}
          max={64000}
          step={1}
          onChange={(max_tokens) =>
            props.onChange({
              ...props.value,
              kwargs: {
                ...kwargs,
                max_tokens: max_tokens === -1 ? null : max_tokens,
              },
            })
          }
          readOnly={props.readOnly}
          variant={props.variant}
        />

        {props.options && (!props.readOnly || !!props.options.stop) && (
          <div className="flex flex-col gap-2">
            <FormLabel>Stop Sequences</FormLabel>
            <ManifestStopEditor
              value={props.options.stop}
              onChange={(stop) =>
                props.onOptionsChange({
                  ...props.options,
                  stop,
                })
              }
              readOnly={props.readOnly}
            />
          </div>
        )}
      </div>
    </>
  );
}

export function ManifestChatGroqPreview(
  props: ManifestPreviewProps<SerializedConstructor>
) {
  const displayLabel = getModelDisplayNameFromIdList(props.value.id);
  const kwargs = props.value.kwargs;
  const model = kwargs.model ?? DEFAULT_MODEL;

  if (props.variant === 'line') {
    return (
      <div className="flex flex-row items-center gap-1">
        <span className="font-semibold">{displayLabel}</span>
        <span>{model}</span>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={props.onClick}
      className={cn(
        'flex w-auto select-none flex-col rounded-md bg-neutral-50 p-4 shadow-sm dark:bg-[rgb(9,9,13)]',
        props.onClick ? 'cursor-pointer' : 'cursor-default'
      )}
    >
      <span className="flex items-center gap-2">
        <span className="font-semibold">{displayLabel}</span>
      </span>

      <span className="mt-3 flex items-center gap-1">
        <Chip variant="outlined" size="sm" color="neutral">
          {model}
        </Chip>

        {kwargs.temperature != null && (
          <Chip variant="outlined" size="sm" color="neutral">
            Temperature: {kwargs.temperature}
          </Chip>
        )}
      </span>
    </button>
  );
}
